import React, {useState} from 'react';
import {Beacon, BeaconConfiguration, BeaconData} from "types/Beacon";
import {Form, Formik, FormikHelpers, FormikProps} from "formik";
import Label from "components/Form/Label";
import Field from "components/Form/Field";
import FormControls from "components/FormControls";
import Selector from "components/Form/Selector";
import Toggle from "components/Form/Toggle";
import Locator from "components/Form/Locator";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import IconName from "types/IconName";
import {useOperator} from "context/OperatorContext";
import {toTitleCase} from "../../../services/string";
import {Item} from "../../../components/Form/Selector/Selector";
import Button from "../../../components/Button";
import {Geometry} from "geojson";
import * as Yup from "yup";
import {ErrorNotification} from "../../../components/Form/ErrorNotification";
import FieldHelp from "../../../components/Form/Form/Field/FieldHelp";
import {ExclamationCircleIcon} from "@heroicons/react/solid";

// Types ----

type BeaconFormProps = {
  beacon?: Beacon
  nextConfig?: BeaconConfiguration
  onSubmit: (values: BeaconData, helpers: FormikHelpers<BeaconData>) => Promise<any>
}

// Helpers ----

const txPowers = [
  {id: 4, name: "4 dBm"},
  {id: 0, name: "0 dBm"},
  {id: -4, name: "-4 dBm"},
  {id: -8, name: "-8 dBm"},
  {id: -12, name: "-12 dBm"},
  {id: -16, name: "-16 dBm"},
  {id: -20, name: "-20 dBm"},
  {id: -40, name: "-40 dBm"},
]

function beaconName(make?: string, model?: string, macAddress?: string): string {
  return macAddress ? `${make} ${model} (${macAddress.toUpperCase()})` : `${make} ${model}`
}

// Component ----

function BeaconForm(props: BeaconFormProps) {
  const {termFor} = useOperator();

  const [mapFlyToGeometry, setMapFlyToGeometry] = useState<Geometry | undefined>();

  const {data: sites, isLoading: sitesLoading} = useQuery(
    ['sites.all', {page: 1}],
    () => ngage.sites.all(1)
  )
  const selectableSites = sites?.data.map(site => ({
    id: site.id,
    name: site.name,
    description: site.address?.city,
    icon: IconName.OFFICE_BUILDING
  })) || []

  //TODO: Make a reusable topic selector component
  const {data: selectableTopics, isLoading: topicsLoading} = useQuery(['topics.all'], async () => {
    const topicSets = await Promise.all([
      ngage.exhibits.filtered({per_page: 1000}).then(page => page.data.map(exhibit => ({
        type: 'exhibit',
        id: `exhibit-${exhibit.id}`,
        name: exhibit.name,
        description: exhibit.type?.name ?? 'Exhibit',
        icon: IconName.LIBRARY
      }))),

      ngage.facilities.filtered({per_page: 1000}).then(page => page.data.map(facility => ({
        type: 'facility',
        id: `facility-${facility.id}`,
        name: facility.name,
        description: facility.type?.name ?? 'Facility',
        icon: IconName.OFFICE_BUILDING
      }))),

      ngage.trails.filtered({perPage: 1000}).then(page => page.data.map(trail => ({
        type: 'trail',
        id: `trail-${trail.id}`,
        name: trail.name,
        description: trail.type ? `${toTitleCase(trail.type)} Trail` : 'Trail',
        icon: IconName.MAP
      }))),
    ]);

    return topicSets.flat().sort((left, right) => left.name > right.name ? 1 : -1) as Item[];
  });

  function updateMacAddress(macAddress: string, formik: FormikProps<BeaconData>) {
    const macAddressFormatted = macAddress
      .toUpperCase()
      .replaceAll(':', '')
      .split('')
      .reduce((carry: string, character: string, index: number, characters: string[]): string => {
        let macAddress = carry + character

        if ((index < (characters.length - 1)) && (index % 2 !== 0)) macAddress += ':'

        return macAddress
      }, '')

    void formik.setFieldValue('mac_address', macAddressFormatted);
  }

  function updateBeaconName(name: string, formik: FormikProps<BeaconData>) {
    void formik.setFieldValue('name', name || beaconName(
      formik.values.make,
      formik.values.model,
      formik.values.mac_address?.replaceAll(':', '')
    ))
  }

  function selectSite(siteId: number | string, {setFieldValue}: FormikHelpers<BeaconData>) {
    void setFieldValue('site_id', Number(siteId));

    // Find the site and fly to it on the map
    const site = sites?.data.find(site => site.id === Number(siteId));
    setMapFlyToGeometry(site?.area || props.beacon?.position);
  }

  function selectTopic(itemId: string, {setFieldValue}: FormikHelpers<BeaconData>) {
    const [type, id] = itemId.split('-')

    void setFieldValue('topic', {
      type: type,
      id: Number(id),
    })
  }

  function clearTopic({setFieldValue}: FormikHelpers<BeaconData>) {
    void setFieldValue('topic', null)
  }

  const validationSchema = Yup.object().shape({
    make: Yup.string().required("This field is required"),
    model: Yup.string().required("This field is required"),
    version: Yup.string().required("This field is required"),
    mac_address: Yup.string()
      .min(12, 'Must be a minimum of 12 characters')
      .max(17, 'Must be a maximum of 17 characters')
      .matches(/(([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2})|[0-9A-Fa-f]{12}/, 'Enter a valid MAC address (e.g. 00:1A:2B:3C:4D:5E)')
      .required('This field is required'),
    name: Yup.string().required("This field is required").max(255, 'Maximum of 255 characters'),
    type: Yup.string().required("This field is required"),
    namespace: Yup.string()
      .length(20, 'Must be exactly 20 characters')
      .matches(/^[0-9a-fA-F]+$/, 'Only letters A to F and numbers allowed')
      .required('This field is required'),
    instance: Yup.string()
      .length(12, 'Must be exactly 12 characters')
      .matches(/^[0-9a-fA-F]+$/, 'Only letters A to F and numbers allowed')
      .required('This field is required'),
    tx_power: Yup.number().required("This field is required"),
    interval: Yup.number().min(100, 'Minimum of 100').max(5000, 'Maximum of 5000').required("This field is required"),
    site_id: Yup.number().notOneOf([0], 'This field is required').required("This field is required"),
    position: Yup.object().required(`Please place the beacon on the map using the drawing tool in the top left corner`),
    battery_level: Yup.number().min(0, "Cannot be less than 0%").max(100, "Cannot be more than 100%").required("This field is required"),
  })

  const initialValues: BeaconData = {
    // Device
    make: props.beacon?.make || 'Minew',
    model: props.beacon?.model || 'I3',
    version: props.beacon?.version || '1.3.04',
    mac_address: props.beacon?.mac_address?.toUpperCase() || '',
    name: props.beacon?.name || '',
    // Adverts
    type: props.beacon?.frame_type || 'eddystone-uid',
    namespace: props.beacon?.namespace || props.nextConfig?.namespace || '',
    instance: props.beacon?.instance || props.nextConfig?.instance || '',
    interval: props.beacon?.interval || 1000,
    tx_power: props.beacon?.tx_power || 0,
    // Location
    site_id: props.beacon?.site.id || 0,
    position: props.beacon?.position || undefined,
    // Status
    battery_level: props.beacon?.battery_level || 100,
    is_enabled: props.beacon?.is_enabled ?? true,
    // Topic
    topic: props.beacon?.topic ? {
      type: props.beacon.topic.type,
      id: props.beacon.topic.id
    } : null
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={props.onSubmit}>
      {formik => {
        return (
          <Form className="space-y-8 divide-y divide-gray-200">
            <ErrorNotification formik={formik} />

            <div className="shadow sm:rounded-md">
              <div className="px-4 py-5 bg-white sm:p-6 sm:rounded-md">
                <div className="space-y-8 divide-y divide-gray-200">
                  <div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Device Information
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Basic details about the beacon device itself
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <div className="mt-1">
                          <Field
                            label='Make'
                            type="text"
                            name="make"
                            id="make"
                            autoComplete="off"
                            maxLength="255"
                            disabled
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <div className="mt-1">
                          <Field
                            label='Model'
                            type="text"
                            name="model"
                            id="model"
                            autoComplete="off"
                            maxLength="255"
                            disabled
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-1">
                        <div className="mt-1">
                          <Field
                            label='Version'
                            type="text"
                            name="version"
                            id="version"
                            autoComplete="off"
                            maxLength="255"
                            disabled
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <div className="mt-1">
                          <Field
                            label='MAC Address *'
                            type="text"
                            name="mac_address"
                            id="mac_address"
                            autoComplete="off"
                            maxLength={17}
                            autoFocus
                            onBlur={(event: React.FormEvent<HTMLInputElement>) => updateMacAddress(event.currentTarget.value, formik)}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-4">
                        <div className="mt-1">
                          <Field
                            label="Name *"
                            hint='255 chars max'
                            type="text"
                            name="name"
                            id="name"
                            autoComplete="off"
                            maxLength="255"
                            onBlur={(event: React.FormEvent<HTMLInputElement>) => updateBeaconName(event.currentTarget.value, formik)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Advert Configuration
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Configure the data included in beacon advertisements
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-2">
                        <Label htmlFor="type">Type</Label>
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="type"
                            id="type"
                            autoComplete="off"
                            maxLength={255}
                            disabled
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-3 md:col-span-2">
                        <Label htmlFor="namespace">Namespace</Label>
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="namespace"
                            id="namespace"
                            autoComplete="off"
                            maxLength={20}
                            disabled
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <Label htmlFor="instance">Instance *</Label>
                        <div className="mt-1">
                          <Field
                            type="text"
                            name="instance"
                            id="instance"
                            autoComplete="off"
                            maxLength={12}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <Selector
                          label='Transmission Power'
                          items={txPowers}
                          selectedId={formik.initialValues.tx_power}
                          onChange={item => formik.setFieldValue('tx_power', item.id)}
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <Label htmlFor="interval">Interval</Label>
                        <div className="mt-1">
                          <Field
                            type="number"
                            name="interval"
                            id="interval"
                            addon="ms"
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Location
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Set where the beacon has been placed
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <Selector
                          label={`${termFor('Site')} *`}
                          isLoading={sitesLoading}
                          items={selectableSites}
                          selectedId={initialValues.site_id}
                          onChange={site => selectSite(site.id, formik)}
                        />

                        {(!!formik.errors.site_id && formik.touched.site_id) &&
                          <FieldHelp isError>
                            {formik.errors.site_id}
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-6 rounded-md overflow-hidden">
                        <span className="block text-sm font-medium text-gray-700">
                            Position *
                        </span>
                        <div className="mt-1">
                          <Locator
                            height={'32rem'}
                            type='position'
                            geometry={initialValues.position}
                            flyToGeometry={mapFlyToGeometry}
                            onChange={geometry => formik.setFieldValue('position', geometry)}
                          />
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                          Place the beacon on the map using the drawing tool
                        </p>

                        {(!!formik.errors.position && formik.touched.position) &&
                          <FieldHelp isError>
                            <div className="flex items-center gap-2">
                              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true"/>
                              {formik.errors.position}
                            </div>
                          </FieldHelp>
                        }
                      </div>

                      <div className="sm:col-span-3 flex space-x-2">
                        <div className="w-full">
                          <Selector
                            label={"Topic"}
                            isLoading={topicsLoading}
                            items={selectableTopics ?? []}
                            selectedId={formik.values.topic ? `${formik.values.topic.type}-${formik.values.topic.id}` : null}
                            onChange={topic => selectTopic(String(topic.id), formik)}
                          />
                          <p className="mt-2 text-sm text-gray-500">
                            (Optional) Choose the topic to associate with this beacon, if any
                          </p>
                        </div>
                        <div className="mt-6">
                          <Button color={Button.colors.SECONDARY} onClick={() => clearTopic(formik)}>
                            Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8">
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Status
                      </h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Configure the current device status
                      </p>
                    </div>
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">

                      <div className="sm:col-span-2">
                        <Label htmlFor="minor">Battery Level *</Label>
                        <div className="mt-1">
                          <Field
                            type="number"
                            name="battery_level"
                            id="battery_level"
                            addon="%"
                            autoComplete="off"
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-6">
                        <Toggle
                          label="Enabled"
                          checked={formik.values.is_enabled}
                          onChange={checked => formik.setFieldValue('is_enabled', checked)}
                        />
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <FormControls formik={formik}/>
            </div>
          </Form>
        )
      }}
    </Formik>
  );
}

export default BeaconForm;